import {
  RevIconButton,
  RevInput,
  RevTooltip,
  RevSelect,
} from "@revmatics/catalyst";
import { faInfoCircle, faTrash } from "@fortawesome/pro-light-svg-icons";
import {
  CompetitorLocalItemType,
  UsernameItem,
} from "../../../../../../../../../stores/types/brandSocialHandlesTypes.tsx";
import { useState } from "react";
import { hasValidDomain } from "../../../../../../../../../helpers/functions/hasValidHelpers.ts";
import { competitorStrengthOptions } from "../../../../data.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlatformContainer } from "../../../platformContainer/PlatformContainer.tsx";
import { PlatformItemType } from "../../../../../../../../../stores/types/profileTypes.tsx";
import {
  addCompetitorUsernameSection,
  removeCompetitorPlatform,
  removeCompetitorUsername,
  selectCompetitorPlatform,
  updateCompetitorUsernameSection,
} from "../../../../../../../../../stores/slice/brandSocialHandlesSlice.tsx";
import { useAppDispatch } from "../../../../../../../../../stores/hooks/hooks.ts";

type Props = {
  order: number;
  displayTrash?: boolean;
  handleRemove?: () => void;
  competitor: CompetitorLocalItemType;
  setWebsite: (website: string) => void;
  setStrength: (strength: string) => void;
  platforms: PlatformItemType[];
};

export const CompetitorWrapper = ({
  order,
  displayTrash,
  handleRemove,
  competitor,
  setWebsite,
  setStrength,
  platforms,
}: Props) => {
  const dispatch = useAppDispatch();

  const [websiteValue, setWebsiteValue] = useState<string>(
    competitor.website || "",
  );
  const [errorWebsite, setErrorWebsite] = useState<string>("");

  const checkWebsite = (value: string) => {
    if (value && !hasValidDomain(value)) {
      setErrorWebsite("Invalid domain");
    } else {
      setErrorWebsite("");
      setWebsite(value);
    }
  };

  return (
    <div className="rounded-lg bg-monochrome-50 w-full px-1 pb-1">
      <div className="px-3 py-[9.5px] flex justify-between items-center ">
        <p className="typography-body-bold-sm text-monochrome-900">
          Competitor #{order}
        </p>
        {displayTrash && (
          <RevIconButton
            icon={faTrash}
            size="xl"
            iconSize="small"
            handleClick={handleRemove}
          />
        )}
      </div>
      <div className="rounded-lg border border-monochrome-200 w-full px-3 py-4 space-y-4 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
          <RevInput
            name="website"
            type="text"
            label="Website"
            size="md"
            value={websiteValue}
            onChange={(event) => setWebsiteValue(event.target.value)}
            onClear={() => {
              setWebsiteValue("");
              setWebsite("");
            }}
            onBlur={(event) => checkWebsite(event.target.value)}
            enableClearButton={competitor.mode === "new"}
            error={errorWebsite}
            wrapperClassName="!shadow-none"
          />
          <div>
            <div className="flex gap-1 items-center">
              <p className="typography-body-xs text-monochrome-900 mb-1">
                Competitor Strength
              </p>
              <div className="flex justify-center text-monochrome-600">
                <RevTooltip
                  content="Rate how directly this competitor competes with your brand. Strong competitors receive higher priority in competitive analysis and audience comparison intelligence."
                  position="topRight"
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-xs hover:text-primary-500"
                  />
                </RevTooltip>
              </div>
            </div>
            <RevSelect
              name={`competitor-strength-${competitor.id}`}
              options={competitorStrengthOptions}
              value={competitor.strength}
              onChange={(value) => {
                if (value && typeof value === "string") {
                  setStrength(value);
                }
              }}
            />
          </div>
        </div>
        <PlatformContainer
          platforms={platforms}
          usernames={competitor.social_usernames}
          selectPlatform={(platformId: number, oldPlatformId?: number) =>
            dispatch(
              selectCompetitorPlatform({
                competitorId: competitor.id,
                oldPlatformId,
                platformId,
              }),
            )
          }
          removePlatform={(platformId: number) =>
            dispatch(
              removeCompetitorPlatform({
                competitorId: competitor.id,
                platformId,
              }),
            )
          }
          removeUsername={(platformId: number, usernameId: number) =>
            dispatch(
              removeCompetitorUsername({
                competitorId: competitor.id,
                platformId,
                usernameId,
              }),
            )
          }
          addUsernameSection={(platformId: number) =>
            dispatch(
              addCompetitorUsernameSection({
                competitorId: competitor.id,
                platformId,
              }),
            )
          }
          updateUsernameSection={(platformId: number, data: UsernameItem) =>
            dispatch(
              updateCompetitorUsernameSection({
                competitorId: competitor.id,
                platformId,
                data,
              }),
            )
          }
          orders={competitor.orders}
        />
      </div>
    </div>
  );
};
